import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Tables_Responsive = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📱 Responsive Tables in CSS</h1>

      <p className="tutorial-intro">
        By default, HTML tables are not responsive. On smaller screens, content
        may overflow and break the layout. With CSS, you can make tables
        scrollable, flexible, or even transform them into stacked cards for
        better mobile readability.
      </p>

      {/* Info Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          ✨ Why Responsive Tables?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Responsive tables ensure your data looks great on desktops, tablets,
          and smartphones. They improve user experience and prevent horizontal
          scrolling issues.
        </p>
      </div>

      {/* Scrollable Table */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#2563eb" }}>
          🔹 Scrollable Table
        </h2>
        <p>
          Wrap the table in a container with <code>overflow-x: auto;</code> to
          allow horizontal scrolling on small screens.
        </p>

        <CodeCopyBlock
          code={`.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}`}
        />

        <div
          className="table-container"
          style={{
            overflowX: "auto",
            border: "2px solid #2563eb",
            marginTop: "1rem",
          }}
        >
          <table style={{ minWidth: "500px", borderCollapse: "collapse" }}>
            <tr>
              <th style={{ border: "1px solid #2563eb", padding: "8px" }}>
                Name
              </th>
              <th style={{ border: "1px solid #2563eb", padding: "8px" }}>
                Age
              </th>
              <th style={{ border: "1px solid #2563eb", padding: "8px" }}>
                City
              </th>
              <th style={{ border: "1px solid #2563eb", padding: "8px" }}>
                Country
              </th>
            </tr>
            <tr>
              <td style={{ border: "1px solid #2563eb", padding: "8px" }}>
                Alice
              </td>
              <td style={{ border: "1px solid #2563eb", padding: "8px" }}>
                22
              </td>
              <td style={{ border: "1px solid #2563eb", padding: "8px" }}>
                Paris
              </td>
              <td style={{ border: "1px solid #2563eb", padding: "8px" }}>
                France
              </td>
            </tr>
          </table>
        </div>
      </div>

      {/* Media Queries */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#16a34a" }}>
          🔹 Responsive with Media Queries
        </h2>
        <p>
          Use <code>@media</code> queries to adjust table layout for smaller
          screens, like reducing font size or stacking rows.
        </p>

        <CodeCopyBlock
          code={`@media (max-width: 600px) {
  table {
    font-size: 12px;
  }

  td, th {
    padding: 6px;
  }
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "2px solid #16a34a",
              fontSize: "14px",
            }}
          >
            <tr style={{ background: "#dcfce7" }}>
              <th style={{ border: "1px solid #16a34a", padding: "8px" }}>
                Product
              </th>
              <th style={{ border: "1px solid #16a34a", padding: "8px" }}>
                Price
              </th>
            </tr>
            <tr>
              <td style={{ border: "1px solid #16a34a", padding: "8px" }}>
                Laptop
              </td>
              <td style={{ border: "1px solid #16a34a", padding: "8px" }}>
                $900
              </td>
            </tr>
          </table>
        </div>
      </div>

      {/* Fluid Width */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#f97316" }}>
          🔹 Fluid Width Table
        </h2>
        <p>
          Use <code>width: 100%</code> and percentage-based columns so the table
          adjusts automatically with screen size.
        </p>

        <CodeCopyBlock
          code={`table {
  width: 100%;
}

td, th {
  width: 33%;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "2px solid #f97316",
            }}
          >
            <tr style={{ background: "#ffedd5" }}>
              <th style={{ border: "1px solid #f97316", padding: "8px" }}>
                Col 1
              </th>
              <th style={{ border: "1px solid #f97316", padding: "8px" }}>
                Col 2
              </th>
              <th style={{ border: "1px solid #f97316", padding: "8px" }}>
                Col 3
              </th>
            </tr>
            <tr>
              <td style={{ border: "1px solid #f97316", padding: "8px" }}>A</td>
              <td style={{ border: "1px solid #f97316", padding: "8px" }}>B</td>
              <td style={{ border: "1px solid #f97316", padding: "8px" }}>C</td>
            </tr>
          </table>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          🚀 Next Steps
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Try combining responsive tables with frameworks like{" "}
          <strong>Bootstrap</strong> or <strong>Tailwind CSS</strong> to make
          them even more mobile-friendly.
        </p>
      </div>
    </div>
  );
};

export default Tables_Responsive;
